<template>
  <div class="step-config-summary-v2" data-testid="step-config-summary-v2__view">
    <!-- ─── 1. Basic Details ─── -->
    <div class="step-config-summary-v2__section">
      <div class="step-config-summary-v2__subtitle">Configuration Summary</div>

      <div class="step-config-summary-v2__section-title">Basic Details</div>
      <div class="step-config-summary-v2__detail">
        File Name: <span>{{ fileDisplayName }}</span>
      </div>
      <div class="step-config-summary-v2__detail">
        Number of File Types: <span>{{ stepOneData.numberOfFileTypes }}</span>
      </div>
      <div class="step-config-summary-v2__detail">
        Config Version: <span>V{{ stepOneData.configVersion || 2 }}</span>
      </div>
    </div>

    <!-- ─── 2. File Types ─── -->
    <div class="step-config-summary-v2__section">
      <div class="step-config-summary-v2__section-title">File Types</div>
      <v-simple-table dense class="step-config-summary-v2__file-types-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Key</th>
            <th>Display Name</th>
            <th>Source File</th>
            <th>Ignore Rows</th>
            <th>Ignore Cols</th>
            <th>Ignore Footer Rows</th>
            <th>Blank Headers</th>
            <th>Keep Duplicate</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(ft, idx) in mergedFileTypes"
            :key="'ft-' + idx"
            data-testid="step-config-summary-v2__file-type-row"
          >
            <td>{{ idx + 1 }}</td>
            <td><code>{{ ft.key }}</code></td>
            <td>{{ ft.name }}</td>
            <td>{{ ft.fileName || '—' }}</td>
            <td>{{ ft.ignorerows || 0 }}</td>
            <td>{{ ft.ignorecolumns || 0 }}</td>
            <td>{{ ft.ignorefooterrows || 0 }}</td>
            <td>{{ ft.blankcolumnheaders || '—' }}</td>
            <td>{{ ft.keepduplicatecolumns ? 'Yes' : 'No' }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- ─── 3. Column Mapping Summary ─── -->
    <div class="step-config-summary-v2__section">
      <div class="step-config-summary-v2__section-title">Column Mapping</div>
      <div class="step-config-summary-v2__mapping-table-wrapper">
        <v-simple-table dense class="step-config-summary-v2__mapping-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Base Column</th>
              <th>Type</th>
              <th>User Column Mapping</th>
              <th
                v-for="ft in stepTwoDataV2.fileTypes"
                :key="'thsum-' + ft.key"
              >
                {{ ft.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(col, idx) in stepTwoDataV2.columnmap"
              :key="'cm-' + idx"
              data-testid="step-config-summary-v2__mapping-row"
            >
              <td>{{ idx + 1 }}</td>
              <td class="font-weight-medium">{{ col.label }}</td>
              <td>{{ col.type }}</td>
              <td>{{ col.userColumnMappingLabel || NULL_MAPPING_DISPLAY }}</td>
              <td
                v-for="ft in stepTwoDataV2.fileTypes"
                :key="'tdsum-' + ft.key + '-' + idx"
              >
                <span
                  :class="{
                    'step-config-summary-v2__null-cell': isNullCell(col, ft.key)
                  }"
                >
                  {{ getHeaderDisplay(col, ft.key) }}
                </span>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </div>

    <!-- ─── 4. Primary Keys ─── -->
    <div class="step-config-summary-v2__section">
      <div class="step-config-summary-v2__section-title">Primary Keys</div>
      <div class="d-flex flex-wrap" style="gap: var(--space-1);">
        <v-chip
          v-for="(pk, idx) in stepTwoDataV2.primaryKeys"
          :key="'pk-' + idx"
          label
          small
          color="primary"
          outlined
          data-testid="step-config-summary-v2__primary-key-chip"
        >
          <span class="font-weight-medium mr-1">{{ idx + 1 }}.</span> {{ pk }}
        </v-chip>
        <span v-if="!stepTwoDataV2.primaryKeys || stepTwoDataV2.primaryKeys.length === 0" class="grey--text">
          None selected
        </span>
      </div>
    </div>

    <!-- ─── 5. Unmapped Columns per File Type ─── -->
    <div class="step-config-summary-v2__section">
      <div class="step-config-summary-v2__section-title">Unmapped Columns</div>
      <div
        v-for="ft in mergedFileTypes"
        :key="'um-' + ft.key"
        class="mb-2"
        data-testid="step-config-summary-v2__unmapped-section"
      >
        <div class="font-weight-medium">{{ ft.name }}</div>
        <div v-if="getUnmappedHeaders(ft).length > 0">
          <v-chip
            v-for="(header, hIdx) in getUnmappedHeaders(ft)"
            :key="'umh-' + ft.key + '-' + hIdx"
            small
            label
            class="ma-1"
            color="blue-grey lighten-5"
            text-color="primary"
          >
            {{ header }}
          </v-chip>
        </div>
        <div v-else class="grey--text text--lighten-1 ml-1" style="font-size: var(--font-size-xs);">
          All columns mapped
        </div>
      </div>
    </div>

    <!-- ─── Actions ─── -->
    <div class="step-config-summary-v2__actions">
      <v-btn
        color="primary"
        @click="onBack"
        data-testid="step-config-summary-v2__back-btn"
      >
        <p>Back</p>
      </v-btn>
      <v-btn
        color="primary"
        @click="onFinish"
        data-testid="step-config-summary-v2__finish-btn"
      >
        <p>Finish</p>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { NULL_MAPPING_DISPLAY } from './utils/constants';

export default {
  name: 'StepConfigSummaryV2',
  props: {
    stepOneData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    stepTwoDataV2: {
      type: Object,
      required: true,
      default: () => ({
        fileTypes: [],
        columnmap: [],
        primaryKeys: [],
        selectedBaseFileTypeKey: '',
        fileTypeEntries: [],
      }),
    },
    selectFileName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      NULL_MAPPING_DISPLAY,
    };
  },
  computed: {
    ...mapGetters('bot', ['fileMasters']),
    fileDisplayName() {
      if (!this.selectFileName) return '—';
      const match = (this.fileMasters || []).find((f) => f._id === this.selectFileName);
      return match ? match.name : this.selectFileName;
    },
    /**
     * Merge fileTypes (has config fields) with fileTypeEntries (has fileName)
     * so the File Types table can show all information in one place.
     */
    mergedFileTypes() {
      const entriesMap = {};
      for (const e of (this.stepTwoDataV2.fileTypeEntries || [])) {
        if (e.key) entriesMap[e.key] = e;
      }
      return (this.stepTwoDataV2.fileTypes || []).map((ft) => ({
        ...ft,
        fileName: (entriesMap[ft.key] || {}).fileName || '',
        headers: (entriesMap[ft.key] || {}).headers || [],
      }));
    },
  },
  methods: {
    getHeaderDisplay(col, fileTypeKey) {
      const alias = col.fileTypeAliases && col.fileTypeAliases[fileTypeKey];
      if (!alias || alias.header === null || alias.header === undefined) {
        return NULL_MAPPING_DISPLAY;
      }
      return alias.header;
    },

    isNullCell(col, fileTypeKey) {
      const alias = col.fileTypeAliases && col.fileTypeAliases[fileTypeKey];
      return !alias || alias.header === null || alias.header === undefined;
    },

    /**
     * Get headers from a file type that are NOT mapped to any column.
     */
    getUnmappedHeaders(ft) {
      if (!ft.headers || !ft.headers.length) return [];
      const mappedHeaders = new Set();
      for (const col of this.stepTwoDataV2.columnmap) {
        const alias = col.fileTypeAliases && col.fileTypeAliases[ft.key];
        if (alias && alias.header) {
          mappedHeaders.add(alias.header);
        }
      }
      return ft.headers.filter((h) => !mappedHeaders.has(h));
    },

    onBack() {
      this.$emit('back');
    },

    onFinish() {
      this.$emit('finish');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './StepConfigSummaryV2.scss';
</style>
